import { SlideHeader } from "../../../SlideHeader";
import { SupLink } from "../../../SlideContext";

const STEPS: Array<{
  n: string;
  label: string;
  detail: string;
  result: string;
  final?: boolean;
}> = [
  {
    n: "1",
    label: "m12 baseline ARR",
    detail: "Anchored on slide 12 SOM: 700 accounts × €184/mo × 12",
    result: "€1.55M",
  },
  {
    n: "2",
    label: "m24 ARR target",
    detail:
      "3–5× year-1 growth — typical for strong-performing seed-stage B2B SaaS",
    result: "€5–7M",
  },
  {
    n: "3",
    label: "ARR multiple",
    detail:
      "Category-defining private B2B SaaS at Series A/B in current market",
    result: "20–25×",
  },
  {
    n: "4",
    label: "m24 enterprise value",
    detail: "Center anchor: €6M ARR × ~21× ≈ €125M",
    result: "€125M",
    final: true,
  },
];

const SENSITIVITY: Array<{ arr: string; m20: string; m25: string }> = [
  { arr: "€5M", m20: "€100M", m25: "€125M" },
  { arr: "€6M", m20: "€120M", m25: "€150M" },
  { arr: "€7M", m20: "€140M", m25: "€175M" },
];

export default function EVTarget() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:pl-24 md:pr-20 md:pt-16 md:pb-10 md:[&_header]:mb-6">
      <SlideHeader unnumbered eyebrow="Appendix G · EV Target">
        <h1 className="text-2xl font-semibold leading-tight tracking-tight md:text-4xl md:leading-[1.15]">
          How we get to{" "}
          <span className="text-blue-700">€125M enterprise value</span> at m24.
        </h1>
        <p className="mt-3 text-xl font-medium tracking-tight text-slate-700 md:mt-4 md:text-2xl">
          Four steps. Anchored on the slide-12 SOM ladder.
        </p>
      </SlideHeader>

      {/* The chain */}
      <div className="space-y-2 md:space-y-3">
        {STEPS.map((s) => (
          <StepRow key={s.n} {...s} />
        ))}
      </div>

      {/* Sensitivity + comp/risk */}
      <div className="mt-5 grid grid-cols-1 gap-4 border-t border-slate-200 pt-5 md:mt-6 md:grid-cols-[2fr_3fr] md:gap-6 md:pt-6">
        {/* Sensitivity table */}
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-blue-700 md:mb-3 md:text-sm">
            Sensitivity — m24 EV (ARR × multiple)
          </p>
          <div className="overflow-hidden rounded-lg ring-1 ring-slate-200">
            <table className="w-full text-sm md:text-base">
              <thead>
                <tr className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 md:text-sm">
                  <th className="px-3 py-2 text-left font-semibold">
                    m24 ARR
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">20×</th>
                  <th className="px-3 py-2 text-right font-semibold">25×</th>
                </tr>
              </thead>
              <tbody>
                {SENSITIVITY.map((row) => {
                  const isAnchor =
                    row.arr === "€5M";
                  return (
                    <tr
                      key={row.arr}
                      className="border-t border-slate-100"
                    >
                      <td className="px-3 py-2 font-medium text-slate-700">
                        {row.arr}
                      </td>
                      <td
                        className={
                          row.arr === "€6M"
                            ? "bg-blue-50 px-3 py-2 text-right font-bold text-blue-700"
                            : "px-3 py-2 text-right tabular-nums text-slate-900"
                        }
                      >
                        {row.m20}
                      </td>
                      <td
                        className={
                          isAnchor
                            ? "bg-blue-50 px-3 py-2 text-right font-bold text-blue-700"
                            : "px-3 py-2 text-right tabular-nums text-slate-900"
                        }
                      >
                        {row.m25}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs italic text-slate-500 md:text-sm">
            Highlighted cells: paths to €125M (either €5M × 25× or €6M × ~21×).
          </p>
        </div>

        {/* Comp + dependencies */}
        <div className="space-y-3">
          <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200 md:p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-700 md:text-sm">
              Comp anchor
            </p>
            <p className="mt-2 text-sm leading-snug text-slate-700 md:text-base">
              <span className="font-semibold text-slate-900">AppTweak Series B</span>{" "}
              (Oct 2022, €20.7M raise) → ~€80–120M post-money. Apptonomy's
              €125M m24 target lands roughly at AppTweak's Series B valuation.
              <SupLink to="sources">13</SupLink>
            </p>
          </div>
          <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200 md:p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-700 md:text-sm">
              Dependencies
            </p>
            <ul className="mt-2 space-y-1 text-sm leading-snug text-slate-700 md:text-base">
              <li>
                <span className="font-semibold text-slate-900">m12 €1.55M ARR must land.</span>{" "}
                If m12 closes at €1M instead, m24 compresses 30–50%.
              </li>
              <li>
                <span className="font-semibold text-slate-900">
                  Growth rate, not just absolute ARR, drives the multiple.
                </span>{" "}
                3×+ YoY commands 20–25×; 1.5× drops it to 8–12×.
              </li>
              <li>
                <span className="font-semibold text-slate-900">
                  Slide-13 expansion narrative must hold.
                </span>{" "}
                Multiples compress if the long-term TAM ceiling is just the
                ASO niche.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function StepRow({
  n,
  label,
  detail,
  result,
  final,
}: {
  n: string;
  label: string;
  detail: string;
  result: string;
  final?: boolean;
}) {
  const wrapClass = final
    ? "rounded-lg bg-blue-50 p-3 ring-2 ring-blue-400 md:p-4"
    : "rounded-lg bg-white p-3 ring-1 ring-slate-200 md:p-4";
  const numClass = final
    ? "flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white md:h-9 md:w-9 md:text-base"
    : "flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-sm font-bold text-slate-700 md:h-9 md:w-9 md:text-base";
  const labelClass = final
    ? "text-sm font-bold uppercase tracking-[0.1em] text-blue-700 md:text-base"
    : "text-sm font-semibold uppercase tracking-[0.1em] text-slate-700 md:text-base";
  const detailClass = final
    ? "mt-0.5 text-xs leading-snug text-blue-900 md:text-sm"
    : "mt-0.5 text-xs leading-snug text-slate-600 md:text-sm";
  const resultClass = final
    ? "text-2xl font-bold tracking-tight text-blue-700 md:text-3xl"
    : "text-2xl font-bold tracking-tight text-slate-900 md:text-3xl";

  return (
    <div className={wrapClass}>
      <div className="flex items-center gap-3 md:gap-4">
        <span className={numClass}>{n}</span>
        <div className="min-w-0 flex-1">
          <p className={labelClass}>{label}</p>
          <p className={detailClass}>{detail}</p>
        </div>
        <p className={resultClass}>{result}</p>
      </div>
    </div>
  );
}
